import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Music, Star, Calendar, MapPin, Mail, Play, ArrowRight, X } from "lucide-react";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import SponsorsBlock from "@/components/SponsorsBlock";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import { BrandInput } from "@/components/ui/BrandInput";
import { usePageSEO } from "@/hooks/use-page-seo";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { buildMusicEventSchema, EVENT_LOCATION, EVENT_ORGANIZER } from "@/lib/event-schema";
import { ARTISTS, getArtistBySlug, getOtherArtists, type ArtistData } from "@/lib/artists";
import { saveEmail, n8nSaveEmail, n8nSaveEmailSS } from "@/lib/firebase";
import { validateEmail } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";

// ────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────

const NoiseLayer = ({ opacity = 0.12 }: { opacity?: number }) => (
  <div
    className="absolute inset-0 pointer-events-none"
    style={{
      opacity,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
    }}
  />
);

const RoostertailBadge = () => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yacht-yellow/95 backdrop-blur-sm text-yacht-charcoal text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase shadow-lg">
    <span className="block w-1.5 h-1.5 rounded-full bg-yacht-charcoal" />
    The Roostertail · Detroit River
  </div>
);

// ── Email Capture (compact, artist-page styled) ───────────────────
const ArtistEmailCapture = ({ artistName }: { artistName: string }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyOnList, setAlreadyOnList] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("yrd_email_submitted") === "true") {
      setAlreadyOnList(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!canSubmit("email")) {
      setError(`Hold on ${getSecondsRemaining("email")}s — try again in a moment.`);
      return;
    }

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await Promise.all([
        saveEmail(email, "stay-in-know"),
        n8nSaveEmail(email, "stay-in-know").catch(console.error),
        n8nSaveEmailSS(email, "stay-in-know").catch(console.error),
      ]);
      localStorage.setItem("yrd_email_submitted", "true");
      recordSubmission("email");
      setSuccess(true);
      setEmail("");
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (alreadyOnList || success) {
    return (
      <div className="rounded-2xl bg-yacht-yellow/15 border border-yacht-yellow/30 px-6 py-7 text-center">
        <p className="text-yacht-charcoal font-bold text-base sm:text-lg">
          {success ? "You're on the list ✓" : "You're already on the list ✓"}
        </p>
        <p className="text-yacht-charcoal/70 text-sm mt-1">
          We'll keep you posted on {artistName} and the rest of the weekend.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl bg-white/8 backdrop-blur-sm border border-white/15 px-5 py-6 sm:px-7 sm:py-8"
    >
      <div className="flex items-center justify-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-yacht-yellow" />
        <p className="text-yacht-yellow font-bold text-xs sm:text-sm uppercase tracking-[0.18em]">
          Stay in the loop
        </p>
      </div>
      <h3 className="text-white text-xl sm:text-2xl font-bold text-center leading-tight mb-1">
        Get news about {artistName}
      </h3>
      <p className="text-white/65 text-sm text-center mb-5">
        Setlist hints. Show updates. Price changes. We email — never spam.
      </p>
      <div className="flex flex-col sm:flex-row gap-2.5">
        <BrandInput
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1 text-base py-3.5"
        />
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-yacht-yellow text-yacht-charcoal font-bold text-base px-6 py-3.5 disabled:opacity-50 hover:brightness-110 transition-all"
        >
          {submitting ? "..." : "Sign Me Up"}
        </button>
      </div>
      {error && (
        <p className="text-red-300 text-xs mt-3 text-center">{error}</p>
      )}
    </form>
  );
};

// ── Pricing Tier Card ────────────────────────────────────────────
const PricingCard = ({
  tier,
  price,
  feeNote,
  blurb,
  features,
  highlight = false,
  onBuy,
  badge,
}: {
  tier: string;
  price: string;
  feeNote: string;
  blurb: string;
  features: string[];
  highlight?: boolean;
  onBuy: () => void;
  badge?: string;
}) => (
  <div
    className={`relative rounded-2xl border p-5 sm:p-6 ${
      highlight
        ? "bg-yacht-yellow text-yacht-charcoal border-yacht-yellow shadow-xl"
        : "bg-yacht-charcoal text-white border-white/10"
    }`}
  >
    {badge && (
      <span
        className={`absolute -top-3 left-5 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] ${
          highlight ? "bg-yacht-charcoal text-yacht-yellow" : "bg-yacht-yellow text-yacht-charcoal"
        }`}
      >
        {badge}
      </span>
    )}
    <div className="flex items-baseline justify-between gap-3 mb-1">
      <h4 className={`font-bold uppercase tracking-tight text-lg sm:text-xl ${highlight ? "" : ""}`}>
        {tier}
      </h4>
      <div className="text-right">
        <p className={`font-bold text-3xl sm:text-4xl leading-none`}>{price}</p>
        <p className={`text-[10px] font-medium opacity-70 uppercase tracking-wider mt-1`}>{feeNote}</p>
      </div>
    </div>
    <p className={`text-sm leading-relaxed mb-4 ${highlight ? "opacity-85" : "opacity-75"}`}>
      {blurb}
    </p>
    <ul className="space-y-2 mb-5">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <Check
            className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlight ? "text-yacht-charcoal" : "text-yacht-yellow"}`}
            strokeWidth={3}
          />
          <span className={`${highlight ? "opacity-90" : "opacity-85"}`}>{f}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={onBuy}
      className={`w-full rounded-full py-3 font-bold text-sm sm:text-base transition-all hover:brightness-110 ${
        highlight
          ? "bg-yacht-charcoal text-yacht-yellow"
          : "bg-yacht-yellow text-yacht-charcoal"
      }`}
      style={{ boxShadow: highlight ? "0 4px 14px rgba(0,0,0,0.2)" : "0 4px 14px hsla(54, 93%, 49%, 0.3)" }}
    >
      Get Tickets →
    </button>
  </div>
);

// ── See Other Artists carousel ───────────────────────────────────
const SeeOtherArtists = ({ currentSlug, currentArtistName }: { currentSlug: string; currentArtistName: string }) => {
  const others = getOtherArtists(currentSlug);

  return (
    <section className="py-16 sm:py-20 px-5 sm:px-8 bg-yacht-cream relative overflow-hidden">
      <NoiseLayer opacity={0.05} />
      <div className="relative max-w-5xl mx-auto">
        <motion.div
          className="text-center mb-3 sm:mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-yacht-blue text-xs sm:text-sm font-bold tracking-[0.25em] uppercase mb-2">
            9 National Acts + Local Bands
          </p>
          <h2 className="text-yacht-charcoal text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.05]">
            Your ticket gets you<br className="hidden sm:block" /> the whole weekend.
          </h2>
          <p className="text-yacht-charcoal/70 text-base sm:text-lg mt-4 max-w-xl mx-auto" style={{ fontFamily: "Georgia, serif" }}>
            {currentArtistName} is one of <span className="font-bold text-yacht-charcoal">9 national acts</span> headlining — plus <span className="font-bold text-yacht-charcoal">local bands</span> all weekend on the Detroit River.
          </p>
        </motion.div>

        <p className="text-center text-yacht-charcoal/55 text-xs uppercase tracking-[0.2em] font-semibold mb-6 sm:mb-8">
          ← Swipe to see them all →
        </p>

        {/* Horizontal scroller on mobile, grid on desktop */}
        <div className="flex md:grid md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-5 px-5 sm:-mx-8 sm:px-8 md:mx-0 md:px-0 md:overflow-visible snap-x snap-mandatory md:snap-none">
          {others.map((artist) => (
            <Link
              key={artist.slug}
              to={`/lineup/${artist.slug}`}
              className="group flex-shrink-0 w-[68%] sm:w-[44%] md:w-auto snap-center"
            >
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-yacht-charcoal">
                <img
                  src={artist.image}
                  alt={artist.imageAlt}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-yacht-charcoal via-yacht-charcoal/40 to-transparent" />
                <div className="absolute top-3 left-3">
                  <span className="inline-block bg-yacht-yellow text-yacht-charcoal text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded">
                    {artist.dayLabel} · Jul {artist.day === "friday" ? "17" : "18"}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                    {artist.name}
                  </h3>
                  {artist.role && (
                    <p className="text-white/65 text-xs mt-0.5 line-clamp-1">{artist.role}</p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <Link
            to="/lineup"
            className="inline-flex items-center gap-2 text-yacht-blue font-bold text-sm sm:text-base hover:gap-3 transition-all"
          >
            See the full lineup
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────
// Main Page
// ────────────────────────────────────────────────────────────────

const ArtistDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const artist = slug ? getArtistBySlug(slug) : undefined;

  // Refs declared regardless to keep hook order stable
  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);

  // Always compute jsonLd (skip body when artist missing)
  const artistJsonLd = useMemo(() => {
    if (!artist) return null;
    return buildMusicEventSchema({
      url: `https://yachtrockdetroit.com/lineup/${artist.slug}`,
      description: `${artist.name} performs live at Yacht Rock Detroit 2026 on ${artist.dayDate} at The Roostertail in Detroit. ${artist.bio}`,
    });
  }, [artist]);

  // Show sticky CTA after user scrolls past the hero
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * 0.7) {
        setShowStickyCta(true);
      } else {
        setShowStickyCta(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  // SEO: only fires once artist exists
  usePageSEO({
    title: artist?.metaTitle || "Artist — Yacht Rock Detroit",
    description: artist?.metaDescription || "Yacht Rock Detroit lineup artist page.",
    keywords: artist?.metaKeywords,
    ogImage: artist?.image,
    jsonLd: artistJsonLd || undefined,
    breadcrumbs: artist
      ? [
          { name: "Home", url: "https://yachtrockdetroit.com/" },
          { name: "Lineup", url: "https://yachtrockdetroit.com/lineup" },
          { name: artist.name, url: `https://yachtrockdetroit.com/lineup/${artist.slug}` },
        ]
      : undefined,
  });

  const openTicketModal = useCallback(() => {
    setShowTicketModal(true);
    // Also click the hidden OnTourHQ trigger if it exists on the page
    const trigger = document.getElementById("modal-trigger-element-id");
    if (trigger) trigger.click();
  }, []);

  if (!artist) {
    return <Navigate to="/lineup" replace />;
  }

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* ── HERO ── full-bleed photo + bold name + date + CTA ── */}
      <section
        ref={headerRef}
        className="relative min-h-[100svh] md:min-h-[90vh] bg-yacht-charcoal overflow-hidden flex flex-col justify-end"
      >
        {artist.imageMobile ? (
          <picture>
            <source media="(max-width: 640px)" srcSet={artist.imageMobile} />
            <img
              src={artist.image}
              alt={artist.imageAlt}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={artist.logoUrl ? { objectPosition: "center 15%" } : undefined}
            />
          </picture>
        ) : (
          <img
            src={artist.image}
            alt={artist.imageAlt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        )}
        {/* Strong dark gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-yacht-charcoal/55 via-yacht-charcoal/20 to-yacht-charcoal/95" />
        <NoiseLayer opacity={0.15} />

        {/* Top: day badge — sits below sticky GlobalHeader (which is ~80-100px tall) */}
        <div className="absolute top-36 sm:top-40 left-1/2 -translate-x-1/2 z-20">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <RoostertailBadge />
          </motion.div>
        </div>

        {/* Bottom: title block */}
        <div className="relative z-20 px-5 sm:px-10 pb-10 sm:pb-16 pt-44">
          <motion.p
            className="text-xs sm:text-sm font-bold tracking-[0.3em] uppercase mb-3 sm:mb-4"
            style={{ color: artist.accentColor || "#f2dc05" }}
            initial={{ opacity: 0, y: 16 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {artist.dayLabel} · July {artist.day === "friday" ? "17" : "18"}, 2026
          </motion.p>

          {artist.logoUrl ? (
            <>
              <motion.img
                src={artist.logoUrl}
                alt={artist.name}
                className="block w-[68%] sm:w-[55%] md:w-[42%] lg:w-[34%] max-w-md mb-4 sm:mb-6"
                style={{
                  filter: `drop-shadow(0 6px 24px ${artist.accentColor || "#f2dc05"}88) drop-shadow(0 4px 10px rgba(0,0,0,0.85))`,
                }}
                initial={{ opacity: 0, y: 16, scale: 0.94 }}
                animate={isHeaderInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 16, scale: 0.94 }}
                transition={{ duration: 0.55, delay: 0.2 }}
              />
              <h1 className="sr-only">{artist.name}</h1>
            </>
          ) : (
            <h1 className="text-white font-bold leading-[0.88] tracking-tight text-[16vw] sm:text-[12vw] md:text-[9vw] lg:text-[7vw] mb-3 sm:mb-4">
              <GradualSpacing
                text={artist.name}
                duration={0.5}
                delayMultiple={0.04}
                className=""
              />
            </h1>
          )}

          {artist.role && (
            <motion.p
              className="text-white/85 text-base sm:text-xl md:text-2xl font-medium leading-snug max-w-2xl"
              initial={{ opacity: 0, y: 16 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              style={{ fontFamily: "Georgia, serif" }}
            >
              {artist.role}
            </motion.p>
          )}

          <motion.div
            className="mt-6 sm:mt-8"
            initial={{ opacity: 0, y: 16 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <button
              onClick={openTicketModal}
              className="inline-flex items-center gap-2 bg-yacht-yellow text-yacht-charcoal font-bold text-base sm:text-lg px-7 py-4 rounded-full hover:brightness-110 transition-all"
              style={{ boxShadow: "0 8px 28px hsla(54, 93%, 49%, 0.5)" }}
            >
              Get Tickets · {artist.dayLabel} July {artist.day === "friday" ? "17" : "18"}
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── BIG STATEMENT ── bold yellow block ── */}
      <section className="relative bg-yacht-yellow py-16 sm:py-24 px-5 sm:px-10 overflow-hidden">
        <NoiseLayer opacity={0.08} />
        <motion.div
          className="relative max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p
            className="text-yacht-charcoal text-3xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight"
          >
            {artist.bigStatement}
          </p>
        </motion.div>
      </section>

      {/* ── SEE OTHER ARTISTS ── moved up: answer "who else is coming?" before price ── */}
      <SeeOtherArtists currentSlug={artist.slug} currentArtistName={artist.name} />

      {/* ── KNOWN FOR ── numbered hit list, dark ── */}
      <section className="bg-yacht-charcoal py-16 sm:py-24 px-5 sm:px-10 relative overflow-hidden">
        <NoiseLayer opacity={0.06} />
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            className="mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 mb-3">
              <Music className="w-4 h-4 text-yacht-yellow" />
              <span className="text-yacht-yellow text-xs sm:text-sm font-bold uppercase tracking-[0.25em]">
                Known For
              </span>
            </div>
            <h2 className="text-white text-3xl sm:text-5xl font-bold leading-tight">
              The songs you'll be singing all night.
            </h2>
          </motion.div>

          <ol className="space-y-4 sm:space-y-5">
            {artist.hits.map((hit, idx) => (
              <motion.li
                key={hit.title}
                className="flex items-start gap-4 sm:gap-6 border-b border-white/10 pb-4 sm:pb-5 last:border-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
              >
                <span className="text-yacht-yellow font-bold text-2xl sm:text-3xl leading-none flex-shrink-0 w-8 sm:w-10 tabular-nums">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-lg sm:text-2xl leading-snug">
                    {hit.title}
                  </p>
                  {hit.context && (
                    <p className="text-white/55 text-xs sm:text-sm mt-1" style={{ fontFamily: "Georgia, serif" }}>
                      {hit.context}
                    </p>
                  )}
                </div>
              </motion.li>
            ))}
          </ol>

        </div>
      </section>

      {/* ── THE STORY ── bio block ── */}
      <section className="bg-yacht-cream py-16 sm:py-24 px-5 sm:px-10">
        <div className="max-w-3xl mx-auto">
          <motion.p
            className="text-yacht-blue text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-3"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            The Story
          </motion.p>
          <motion.h2
            className="text-yacht-charcoal text-3xl sm:text-5xl font-bold leading-tight mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {artist.shortTagline}
          </motion.h2>
          <motion.p
            className="text-yacht-charcoal/80 text-base sm:text-lg leading-relaxed"
            style={{ fontFamily: "Georgia, serif" }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            {artist.longBio}
          </motion.p>
        </div>
      </section>

      {/* ── SHOW DETAILS ── date + venue ── */}
      <section className="bg-yacht-blue py-14 sm:py-20 px-5 sm:px-10 relative overflow-hidden">
        <NoiseLayer opacity={0.1} />
        <div className="relative max-w-3xl mx-auto">
          <motion.p
            className="text-yacht-yellow text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-3 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            See {artist.name} Live
          </motion.p>
          <motion.h2
            className="text-white text-3xl sm:text-5xl font-bold leading-tight mb-8 sm:mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            On the river. One {artist.dayLabel}.
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-5 sm:p-6">
              <Calendar className="w-5 h-5 text-yacht-yellow mb-3" />
              <p className="text-white/60 text-xs uppercase tracking-wider font-bold mb-1">When</p>
              <p className="text-white font-bold text-lg sm:text-xl leading-tight">{artist.dayDate}</p>
              <p className="text-white/70 text-sm mt-1">
                {artist.day === "friday" ? "Doors 3:00 PM — close 12:00 AM" : "Doors 12:00 PM — close 12:00 AM"}
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 p-5 sm:p-6">
              <MapPin className="w-5 h-5 text-yacht-yellow mb-3" />
              <p className="text-white/60 text-xs uppercase tracking-wider font-bold mb-1">Where</p>
              <p className="text-white font-bold text-lg sm:text-xl leading-tight">The Roostertail</p>
              <p className="text-white/70 text-sm mt-1">
                100 Marquette Dr · Detroit, MI · On the Detroit River
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── three cards ── */}
      <section className="bg-yacht-charcoal py-16 sm:py-24 px-5 sm:px-10 relative overflow-hidden">
        <NoiseLayer opacity={0.06} />
        <div className="relative max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-yacht-yellow text-xs sm:text-sm font-bold uppercase tracking-[0.25em] mb-3">
              Now Pick Your Pass
            </p>
            <h2 className="text-white text-3xl sm:text-5xl font-bold leading-tight mb-4">
              You've seen what you're getting.
            </h2>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              9 national acts + local bands. 2 days. Floating DJ Deck on the river.<br className="hidden sm:block" /> Single-day or all-in — your call.
            </p>
          </motion.div>

          {/* Quick value-stack reminder right above prices */}
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-8 sm:mb-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {[
              { num: "9", label: "National acts" },
              { num: "2", label: "Days on the river" },
              { num: "1", label: "Floating DJ deck" },
              { num: "∞", label: "Yacht rock hits" },
            ].map((item) => (
              <div key={item.label} className="rounded-xl bg-white/5 border border-white/10 p-3 sm:p-4 text-center">
                <p className="text-yacht-yellow text-2xl sm:text-3xl font-bold leading-none tabular-nums">
                  {item.num}
                </p>
                <p className="text-white/60 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mt-1 leading-tight">
                  {item.label}
                </p>
              </div>
            ))}
          </motion.div>

          <p className="text-white/50 text-xs sm:text-sm text-center -mt-4 sm:-mt-6 mb-8 sm:mb-10 italic">
            …plus local bands all weekend long.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            <PricingCard
              tier="Single Day"
              price="$149"
              feeNote="+ fees"
              blurb={`Just ${artist.dayLabel}. All ${artist.dayLabel === "Friday" ? "3" : "6"} acts that day. Floating DJ Deck. Full venue.`}
              features={[
                `Every act on ${artist.dayLabel}`,
                "The Floating DJ Deck",
                "Full open-area access",
                "Food & drinks for purchase",
              ]}
              onBuy={openTicketModal}
              badge="New"
            />
            <PricingCard
              tier="2-Day GA"
              price="$219"
              feeNote="+ fees"
              blurb="Both days. All 9 national acts + local bands. The whole weekend on the river."
              features={[
                "All 9 national acts + local bands",
                "The Floating DJ Deck",
                "Run of the Roostertail",
                "Food & drinks for purchase",
              ]}
              highlight
              badge="Best Value"
              onBuy={openTicketModal}
            />
            <PricingCard
              tier="VIP Admiral"
              price="$450"
              feeNote="+ fees"
              blurb="Both days. Open bar. Free food. Roostertail Lounge. Priority entry."
              features={[
                "All 9 national acts + local bands",
                "Open bar all weekend",
                "Free food all weekend",
                "Admirals Lounge access",
              ]}
              onBuy={openTicketModal}
            />
          </div>

          <p className="text-white/40 text-xs text-center mt-6">
            All prices in USD · Fees calculated at checkout
          </p>
        </div>
      </section>

      {/* ── EMAIL CAPTURE ── deep teal background ── */}
      <section className="bg-yacht-deepteal py-16 sm:py-20 px-5 sm:px-10 relative overflow-hidden">
        <NoiseLayer opacity={0.07} />
        <div className="relative max-w-xl mx-auto">
          <ArtistEmailCapture artistName={artist.name} />
        </div>
      </section>

      {/* ── BOTTOM CTA ── final push ── */}
      <section className="bg-yacht-yellow py-16 sm:py-24 px-5 sm:px-10 relative overflow-hidden">
        <NoiseLayer opacity={0.07} />
        <div className="relative max-w-3xl mx-auto text-center">
          <motion.p
            className="text-yacht-charcoal/70 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Don't miss it
          </motion.p>
          <motion.h2
            className="text-yacht-charcoal text-4xl sm:text-6xl md:text-7xl font-bold leading-[0.95] tracking-tight mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {artist.name}.<br />
            {artist.dayLabel}. July {artist.day === "friday" ? "17" : "18"}.
          </motion.h2>
          <button
            onClick={openTicketModal}
            className="inline-flex items-center gap-2 bg-yacht-charcoal text-yacht-yellow font-bold text-base sm:text-lg px-8 py-5 rounded-full hover:brightness-110 transition-all"
            style={{ boxShadow: "0 8px 28px rgba(0,0,0,0.25)" }}
          >
            Get Tickets Now
            <ArrowRight className="w-5 h-5" />
          </button>

          <div className="max-w-3xl mx-auto mt-14 sm:mt-20">
            <SponsorsBlock />
          </div>
        </div>
      </section>

      <Footer />

      {/* ── STICKY MOBILE BOTTOM CTA ── */}
      {showStickyCta && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          transition={{ duration: 0.3 }}
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-yacht-charcoal/95 backdrop-blur-md border-t border-white/10 px-4 py-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <button
            onClick={openTicketModal}
            className="w-full rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base py-3.5 active:scale-[0.98] transition-transform"
            style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
          >
            Get Tickets · July {artist.day === "friday" ? "17" : "18"}
          </button>
        </motion.div>
      )}

      {/* ── TICKETS MODAL (in-page popup) ── */}
      {showTicketModal && (
        <TicketModalOverlay artist={artist} onClose={() => setShowTicketModal(false)} />
      )}

      {/* Hidden OnTourHQ trigger — mirrors /tickets pattern for when we wire up checkout */}
      <button
        id="modal-trigger-element-id"
        aria-hidden="true"
        style={{ position: "fixed", left: "-9999px", width: "1px", height: "1px", opacity: 0.01 }}
      />
    </div>
  );
};

// ── In-page ticket popup modal ───────────────────────────────────
// Lightweight popup that gives users a clear pricing recap and links them
// straight to the OnTourHQ checkout flow on /tickets.
const TicketModalOverlay = ({ artist, onClose }: { artist: ArtistData; onClose: () => void }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="relative w-full sm:max-w-md bg-yacht-cream rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-yacht-charcoal/10 hover:bg-yacht-charcoal/20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-yacht-charcoal" />
        </button>

        {/* Header art */}
        <div className="relative h-32 sm:h-36 bg-yacht-charcoal overflow-hidden">
          <img
            src={artist.image}
            alt={artist.imageAlt}
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-yacht-cream via-transparent to-transparent" />
          <div className="relative h-full flex items-end p-5">
            <div>
              <span className="inline-block text-yacht-yellow text-[10px] font-bold uppercase tracking-[0.2em] mb-1">
                {artist.dayLabel} · July {artist.day === "friday" ? "17" : "18"}, 2026
              </span>
              <h3 className="text-white text-2xl font-bold leading-tight drop-shadow-lg">
                See {artist.name}
              </h3>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-3">
          {[
            { label: "Single Day", note: "Friday or Saturday", price: "$149", isHighlight: false },
            { label: "2-Day GA", note: "Both days · all 9 national acts", price: "$219", isHighlight: true },
            { label: "VIP Admiral", note: "Open bar + free food", price: "$450", isHighlight: false },
          ].map((tier) => (
            <Link
              key={tier.label}
              to="/tickets"
              className={`flex items-center justify-between gap-3 rounded-xl px-4 py-4 transition-all ${
                tier.isHighlight
                  ? "bg-yacht-yellow border border-yacht-yellow shadow-md"
                  : "bg-white border border-yacht-charcoal/10 hover:border-yacht-blue/40"
              }`}
            >
              <div className="min-w-0">
                <p className="text-yacht-charcoal font-bold text-sm sm:text-base leading-none">
                  {tier.label}
                </p>
                <p className="text-yacht-charcoal/65 text-xs mt-1">{tier.note}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-yacht-charcoal font-bold text-lg sm:text-xl leading-none">
                  {tier.price}
                </p>
                <p className="text-yacht-charcoal/50 text-[10px] uppercase tracking-wider mt-1">+ fees</p>
              </div>
            </Link>
          ))}

          <Link
            to="/tickets"
            className="block w-full text-center rounded-full bg-yacht-charcoal text-white font-bold text-base py-4 mt-4 hover:brightness-125 transition-all"
          >
            Continue to Checkout →
          </Link>

          <p className="text-yacht-charcoal/55 text-[11px] text-center mt-2 leading-relaxed">
            All passes are sold through OnTourHQ. Mobile tickets — just show the QR at the gate.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ArtistDetail;
